/**
 * Rules for a SCHEDULED pickup ("Запланировать прибытие курьера").
 *
 * Kept pure so the 20-minute floor can be unit tested without mounting the
 * options panel — it is the one rule here the backend also enforces, so a
 * client that gets it wrong turns a fixable mistake into an opaque server
 * error.
 */

/** Minutes a scheduled pickup must be ahead of now. The source's own floor. */
export const LEGACY_SCHEDULE_MIN_MINUTES = 20;

/** Minutes ahead the source pre-fills when the toggle is switched on. */
export const LEGACY_SCHEDULE_DEFAULT_MINUTES = 25;

const MS_PER_MINUTE = 60_000;

/** `YYYY-MM-DDTHH:mm`, the value a `datetime-local` input holds. */
export function toDateTimeLocal(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

/** The value the toggle seeds: now + 25 minutes, as the source does. */
export function defaultScheduledAt(now: Date = new Date()): string {
  return toDateTimeLocal(
    new Date(now.getTime() + LEGACY_SCHEDULE_DEFAULT_MINUTES * MS_PER_MINUTE),
  );
}

/** The earliest selectable moment, for a `min` attribute. */
export function earliestScheduledAt(now: Date = new Date()): string {
  return toDateTimeLocal(
    new Date(now.getTime() + LEGACY_SCHEDULE_MIN_MINUTES * MS_PER_MINUTE),
  );
}

/**
 * Whether a scheduled pickup is too soon.
 *
 * Transcribed from the source's `validateTime`: the floor applies only when the
 * chosen date is TODAY — a future date is always fine, however early its time
 * of day. An empty or unparseable value is not an error, it just means "as soon
 * as possible".
 */
export function isScheduledTooSoon(value: string, now: Date = new Date()): boolean {
  if (value.trim() === '') return false;

  const chosen = new Date(value);
  if (Number.isNaN(chosen.getTime())) return false;

  // Only same-day times are floored, matching `now.isSame(selectedDate,'day')`.
  const sameDay =
    chosen.getFullYear() === now.getFullYear() &&
    chosen.getMonth() === now.getMonth() &&
    chosen.getDate() === now.getDate();
  if (!sameDay) return chosen.getTime() < now.getTime();

  return chosen.getTime() < now.getTime() + LEGACY_SCHEDULE_MIN_MINUTES * MS_PER_MINUTE;
}
